import {React, moment, lockr, uiConstants, _, axios} from '@api'
import {assortmentBoardTemplate} from '@modules/pdf/templates/restoration-hardware/AssortmentBoardTemplate'
import {EsTotal} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {CancelTokenSource} from 'axios'
import {computedFn} from 'mobx-utils'
import {IconName} from '@blueprintjs/core'
import {AssortmentRootStore, GroupByGroupsQueryManager, GroupByGroupStore, IGroupByQueryManagerResult, StoresInstanceContext, UNASSIGNED, useStores, utils} from '@modules'
import {ProductLikeDefinition} from '@modules/common/graphql'
import {Document, pdf as rendererPDF, PDFDownloadLink} from '@react-pdf/renderer'
import {site, ICatalogFieldConfig, ui, PdfExportConfig, PdfPageProps, PdfLocalInfo, PdfSizeDocument, DetailsFieldComputedType, onSiteError, StoresInstance, globalStores, ICardViewSettings} from '@stores'
import {guid} from '@utils'
import {styled} from '@pdf'
import {action, autorun, computed, observable, reaction, runInAction, toJS, trace} from 'mobx'
import {IDataMapped} from 'centric-ui-components'
import {CatalogPdfViewerStore, EmptyPdfPage, PdfDownloadStore, PdfUndoStore, PDFHeroCardStore} from './'
import {ReportPage, vlpReportTemplate} from './templates'
import {FILTER_UNASSIGNED_ID} from '@design-board/components'
import {A4_PAGE_SIZE} from './global'
import {VipLocalStore} from '@modules'
import css from './PdfExportStore.css'

export const DEBUG_PDF = false && DEBUG
export const PDF_MAX_ITEMS = 500 // Need to update index setting if changed

export interface IPdfProductSearchResult {
  items: ProductLikeDefinition[],
  total: EsTotal
}

// TODO - remove for composite queries
export interface IPdfGroupBySearchResult_Group extends Partial<IDataMapped> {
  key: string,
  keys?: string[]
  key_as_string?: string,
  items: { items: Array<ProductLikeDefinition>, total: EsTotal },
  children?: Array<IPdfGroupBySearchResult_Group>
  total_doc_count: EsTotal
  _source: any
  spec?: any
  groupName?: string
  limited?: number
  count?: number
}

export interface IPdfGroupBySearchResult {
  //groups: Array<IGroupAggregateData | IPdfGroupBySearchResult_Group>
  groups: Array<IPdfGroupBySearchResult_Group>
  total: EsTotal
}

export type IPdfSearchResult = IPdfProductSearchResult | IPdfGroupBySearchResult

export interface PdfReportTemplate {
  renderProductPage?: (props: { products: ProductLikeDefinition[], itemsPerRow: number, group?: string }) => React.ReactNode;
  renderCard?: (props) => React.ReactNode;
  label?: string
  renderReportTemplate: (props) => React.ReactNode
  renderPage?: (props) => React.ReactNode
  defaults?: Partial<PdfExportConfig>
}

// const vlpProductReportTemplate = {
//   ysl: vlpReportTemplate,
//   vlp: vlpReportTemplate
// }

// const ReportTemplateByCustomer = {
//   ASICS: vlpProductReportTemplate,
//   YSL: vlpProductReportTemplate
// }

export class PdfExportStore extends VipLocalStore {
  _toDispose = []

  constructor(stores: StoresInstance) {
    super(stores)

    this.query = this.DEFAULT_PDF_QUERY
    this.undo = new PdfUndoStore(stores)
    this.download = new PdfDownloadStore(stores)

    // Let the stores initialize first
    synchronizeStores(() => {
      globalStores['pdf'] = this

      const {settings, catalog, _toDispose} = this
      if (settings) {
        _.merge(this.config, this.reportTemplate?.defaults, settings)
      }

      this._groupQueryManager = new GroupByGroupsQueryManager(stores)

      /**
       * !!! IMPORTANT !!!
       * We can only be rendering one instance of the PDF at a time or else reentrancy errors will occur like:
       *  TypeError: Cannot read property 'dereference' of undefined
       *     at __nbind_free_external
       *
       *
       */
      _toDispose.push(
        autorun(async () => {
          const {generatePDF} = this,
            {
              catalog: {cardView, settings: {showCardLabels}, imageSource, groupBy: {hierarchy, hierarchyGroups, groupingByName}},
              //  filter: {state: {groups}}, // YFTS-3681
              //catalog: {loadingCounts}
              reportTemplate, updateViewKey,
              config,
              products, query: {loading},
              selectedPdfDocument
            } = this

          if (config) {
            const {sections: {specGlossary, cover}, selectedTemplate, imageSize, watermark} = config

            if (selectedPdfDocument) {
              const {author, title, subject} = selectedPdfDocument
            }

            if (hierarchyGroups.every(_.identity)) {
              // YFTS-3335 Touch order by observables to trigger new PDF
              hierarchy.map(h => groupingByName.get(h)?.orderBy)

              await runInAction(async () => {
                //console.log({isFrozen, ignoreRerender})
                const {isFrozen, document, undo: {isDirty}} = this

                if ((isFrozen && (document || isDirty)) || loading /*|| loadingCounts*/) return

                //trace()
                await generatePDF()
              })
            }
          }
        }, {
          name: `Generate PDF due to observable changes`,
          delay: 1 //Fix for YFTS-3710 // Give the system time to settle so we don't reenter during PDF creation
        }),

        autorun(() => {
          const localDocument: PdfLocalInfo = catalog.settings.clientPdf.undoState ?? {}
          if (this.config == null && this.defaultReportTemplateForCustomer) {
            this.config = _.merge(this.defaultReportTemplateForCustomer, {
              document: [...(this.config?.document ?? this.defaultReportTemplateForCustomer.document ?? []), ...(localDocument?.document ?? [])],
              sections: {
                ...(this.defaultReportTemplateForCustomer.sections ?? {}), ...(localDocument?.config?.sections ?? this.defaultReportTemplateForCustomer.sections ?? {})
              }
            })
          }
        }),
        reaction(() => catalog.isShowingClientPDF, showPdf => {
          runInAction(() => {
            this.undo.saveUndoState()
            const {catalog: {settings: {imageRatio}}} = this
            if (this.config) {
              this.config.imageRatio = imageRatio
            }
            this.isFrozen = !showPdf ? this.isFrozen : false
          })
        }),

        reaction(() => catalog.settings.imageRatio, ir => {
          this.undo.config && (this.undo.config.imageRatio = ir)
        }),

        autorun(() => {
          const {reportTemplate} = this
          reportTemplate && runInAction(() => {
            const {config, settings} = this
            if (_.isEmpty(settings) || settings.selectedTemplate != config?.selectedTemplate) {
              _.merge(config, reportTemplate?.defaults)
            }
          })
        }, {name: `Load defaults when template changes`}),

        autorun(() => {
          const {config} = this
        }, {name: `Serialize to local storage when template changes`}),

        autorun(() => {
          const {reportTemplate, config} = this
          if (reportTemplate && config) {
            const {visibleCardFields} = this
            runInAction(() => {
              this.visiblePdfCardFields = _.clone(visibleCardFields)
            })
          }
        }, {name: `Set initial value for visible card fields`}),
        autorun(() => {
          const {reportTemplate, config} = this
          if (reportTemplate && config) {
            const {visiblePdfCardFields, rowsPerPage, catalog: {groupBy: {isProductView}}} = this
            runInAction(() => {
              if (rowsPerPage <= 0 && visiblePdfCardFields.length) {
                visiblePdfCardFields.pop()
              }
            })
          }
        }, {name: `Set visible card fields`}),

        //   useEffect(() => autorun(() => {
        //     trace()
        //
        //     const {pdf: {queryOptions, config, undo, undo: {config: nextConfig}},
        //            dashboard: { cardView, imageSpec, groupBy: { hierarchy, groupingByName } }} = stores
        //
        //     const deps                                                                   = Object.assign({}, toJS(queryOptions), toJS(config), toJS(nextConfig))  // touch dependencies that should freeze rendering
        //
        //     runInAction(() => {
        //       const {pdf: {isCreatingPdf, isRenderingPdf, isFrozen, query: {data}}} = stores
        //
        //       // ignore isFrozen true -> false && data null -> something
        //       if (isCreatingPdf || isRenderingPdf || isFrozen || !data) return
        //
        //       const {pdf: {undo}, nav: {queryParams}} = stores
        //
        //       console.log(`PDF Freeze due to change`, {old: toJS(undo.originalQueryParams), queryParams, deps, queryOptions})
        //     })
        // }, {name: `Freeze PDF rerenders until user confirms changes - save off original data`}), [])

        reaction(() => {
            const {query: {data}, undo: {isDirty}, document} = this
            return data && isDirty && document
          },
          (shouldFreeze) => {
            if (shouldFreeze) {
              this.isFrozen = true
            }
          }
          , {name: `Freeze PDF if user changed something during initial data load when we don't want to freeze apollo`}),

        autorun(() => {
          this.undo.config = toJS(this.config)
        }, {name: `Save off real pdf config (from URL) into copy in store for future state (apply/cancel)`})
      )

      // reaction(
      //   () => this.show,
      //   (show) => show && this.undo.commit(),
      //   {name: `Commit any outstanding PDF changes when the user toggles ON the mode`})
    })
  }

  /// region Document Render
  @action onDocumentRender = (opts?) => {
    //console.log(`PDF has rendered`)
    this.isRenderingPdf = false
    //console.log(`rendering PDF`, opts)
    DEBUG_PDF && console.log(`PDF Rendered`)

    this._opts = opts
    this.initialPdfRender = false
    this.mark_endPdfCreation({pages: opts?.layoutData?.children?.length ?? 0})

    // YFTS-6431 iframe blocks view option reordering
    // if we set this attribute too quickly the PDF won't render at all
    setTimeout(() => {
      document.querySelectorAll(`[data-name='pdf-viewer'] iframe`).forEach((node: HTMLIFrameElement) => node.setAttribute('sandbox', 'allow-scripts'))
    }, 5000)
  }
  @action generatePDF = async () => {
    const {
      catalog: {cardView, imageSource, stores, groupBy: {hierarchy}},
      //catalog: {loadingCounts}
      reportTemplate, updateViewKey,
      config: {sections: {specGlossary, cover}, selectedTemplate, imageSize, watermark},
      products, query: {loading},
      isCreatingPdf, isRenderingPdf,
      selectedPdfDocument: {author, title, subject}
    } = this

    //console.log(`Want to create this...`)

    if (!isCreatingPdf) {
      //console.log(`Starting to create this...`)
      this.hide = true
      runInAction(`Unload previous PDF`, () => {
        //this.document && console.log('Unload previous PDF')
        this.document = null
        //this.document && console.log('Begin create PDF')
        this.isCreatingPdf = true
        this.isRenderingPdf = false
        this.freeze()
        this.mark_startPdfCreation()
        //updateViewKey()

        DEBUG_PDF && console.debug(`${this.catalog.debugId} - PDF Generation started`)
        // if (this.pdfDocContents) {
        //   updateViewKey()
        // }
      })

      setTimeout(async () => {
        await utils.suppressChromeSetTimeoutTookWarning()
        runInAction(`Create PDF`, () => {
          try {
            const result = reportTemplate
                           ? reportTemplate.renderReportTemplate({data: products, download: true})
                           : <EmptyPdfPage/>

            //console.log(`Document END: Create PDF Doc`)

            updateViewKey()
            this.document = this.getPdfDocument(result, {key: this.viewerKey, onRender: this.onDocumentRender})
            // if (this.isFrozen) {
          } catch (err) {
            onSiteError(err)
          } finally {
            this.isCreatingPdf = false
            this.isRenderingPdf = true

            // }
            // DEBUG && console.debug(`Waiting for PDF to render`)
            this.hide = false
          }
        })
      }, 0)
    } else {
      //console.log('Already creating this...')
    }
  }

  // endregion

  // region PDF Freezing - Don't allow requeries or updates to PDF until countdown happens OR user manually clicks OR first render
  get isFrozen() {
    return this.catalog.pdfCatalogStore.isFrozen
  }

  set isFrozen(v) {
    this.catalog.pdfCatalogStore.isFrozen = v
  }

  @observable isCreatingPdf = false
  @observable isRenderingPdf = false
  @observable downloading = false
  @observable visiblePdfCardFields = []
  @observable selectedPageSize = A4_PAGE_SIZE
  @observable selectedLogo = 'Asics'

  document: any // PDF Document - do not observe

  @observable hide = false

  @action freeze() {
    if (!this.isFrozen) {
      this.undo.saveUndoState()
      DEBUG_PDF && console.debug(`${this.catalog.debugId} - Freezing PDF and saving undo state`)
      this.isFrozen = true
    }
  }

  @computed get headerImage() {
    const {catalog: {pdfConfig: {defaultTemplate}}} = this
    return defaultTemplate?.header?.image
  }

  @computed get footerImage() {
    const {catalog: {pdfConfig: {defaultTemplate}}} = this
    return defaultTemplate?.footer?.image
  }

  readonly undo: PdfUndoStore
  readonly download: PdfDownloadStore

  //endregion

  readonly DEFAULT_PDF_QUERY = {data: null, loading: true}

  @observable.shallow query: { data: any, loading: boolean }

  @action _resetPDF = () => {
    const {catalog: {pdfConfig: {defaultTemplate}}} = this
    this.document = null
    this.query = this.DEFAULT_PDF_QUERY
    this.config = defaultTemplate
  }

  @computed get products() {
    return this?.query?.data
  }

  // endregion

  get settings() {
    return this.catalog.settings.clientPdf
  }

  @computed get inactiveLineHeight() {
    const {cardWidth, imageHeight} = this
    return (Math.floor(Math.sqrt(Math.pow(cardWidth, 2) + Math.pow(imageHeight, 2))))
  }

  @computed get inactiveDegree() {
    const {inactiveLineHeight, imageHeight} = this
    return 90 - (Math.asin(imageHeight / this.inactiveLineHeight) * 180) / Math.PI
  }

  @computed get defaultReportTemplateForCustomer(): PdfExportConfig {
    const {workspaces: {deploymentConfig}, catalog, catalog: {pdfConfig}} = this
    if (!deploymentConfig) {
      return null
    }

    if (pdfConfig) {
      if (pdfConfig?.defaultTemplate) {
        const defaultTemplate = _.cloneDeep(pdfConfig?.defaultTemplate)

        return {
          imageRatio: catalog?.settings?.imageRatio ? catalog?.settings?.imageRatio : defaultTemplate.imageRatio,
          ...defaultTemplate
          // layout: 'column'
          /*layout: 'column'
          columns: 'compact',*/
          /*queryLimit: 20,
          group: {
            spec: {
              style: {
                backgroundColor: '#fefbe2',
                border: '1px solid #dcd6d6'
              }
            }
          }*/
          // columns: 'compact',
          /*document: [
            {
              size: 'A4',
              pageHeight: 515,
              author: '',
              title: '',
              subtitle: '',
              subject: '',
              orientation: 'landscape',
              brand: 'Asics'
            },
            {
              size: 'A3',
              pageHeight: 763,
              author: '',
              title: '',
              subtitle: '',
              subject: '',
              orientation: 'landscape',
              brand: 'Asics'
            }
          ],*/
        }
      }
    }

    return null

    /*return {
      imageSize: 'smallImage',
      imageRatio: stores?.catalog?.imageRatio ? stores?.catalog?.imageRatio : 'square',
      columns: 'standard',
      selectedSection: 'document',
      watermark: false,
      queryLimit: 50,
      customAttributes: {},
      groupByCustomAttributes: {},
      // selectedTemplate: CUSTOMER == 'asics' ? 'asicsFootWearThree' : 'ysl',
      selectedTemplate: 'vlp',
      document: {
        size: 'A4',
        pageHeight: 515,
        author: '',
        title: '',
        subtitle: '',
        subject: '',
        orientation: 'landscape'
        // brand: 'Asics'
      },
      showLogo: true,
      // showLabel: true,
      showTotalCount: true,
      showFilters: true,
      showCount: true,
      showHrGroupingLines: true,
      showVtGroupingLines: true,
      sections: {
        cover: false,
        products: true
      },
      header: {
        show: true,
        showGroupBy: false,
        title: null
      },
      footer: {
        show: true,
        showPage: true
      }
      //
      // ...(customer?.name === 'ASICS' ? {
      //   // selectedTemplate: CUSTOMER == 'asics' ? 'asicsFootWearThree' : 'ysl',
      //   selectedTemplate: 'vlp',
      //   document: {
      //     size: 'A4',
      //     pageHeight: 515,
      //     author: '',
      //     title: '',
      //     subtitle: '',
      //     subject: '',
      //     orientation: 'landscape',
      //     brand: 'Asics'
      //   },
      //   showLogo: true,
      //   showLabel: true,
      //   showTotalCount: true,
      //   showFilters: true,
      //   showCount: true,
      //   showHrGroupingLines: true,
      //   showVtGroupingLines: true,
      //   sections: {
      //     cover: false,
      //     products: true
      //   },
      //   header: {
      //     show: true,
      //     showGroupBy: false,
      //     title: null
      //   },
      //   footer: {
      //     show: true,
      //     showPage: true
      //   }
      // } : {
      //   selectedTemplate: 'vlp',
      //   showLogo: true,
      //   showLabel: true,
      //   showTotalCount: true,
      //   showFilters: true,
      //   showCount: true,
      //   showHrGroupingLines: true,
      //   showVtGroupingLines: true,
      //   document: {
      //     size: 'A4',
      //     pageHeight: 515,
      //     author: '',
      //     title: 'Test Report',
      //     subtitle: '',
      //     subject: '',
      //     orientation: 'landscape'
      //   },
      //   sections: {
      //     cover: false
      //   },
      //   header: {
      //     show: true,
      //     showGroupBy: false,
      //     title: null
      //   },
      //   footer: {
      //     show: true,
      //     showPage: true
      //   }
      // })
    }*/
  }

  get reportTemplates(): { [key: string]: PdfReportTemplate } {
    return {'vlp': vlpReportTemplate, 'rh': assortmentBoardTemplate} // ReportTemplateByCustomer[stores?.site?.appConfig?.customer?.name] ?? {}
  }

  get templates() {
    return this.catalog.pdfConfig.templates
  }

  readonly sections: { [key: string]: { label?: string, icon: IconName } } = {
    document: {
      icon: 'book'
    },
    header: {
      icon: 'header'
    },
    footer: {
      icon: 'widget-footer'
    },
    data: {
      label: 'Data Source',
      icon: 'database'
    }
  }

  get config(): PdfExportConfig {
    return this.settings.config
  }

  set config(v) {
    this.settings.config = v
  }

  viewStore: CatalogPdfViewerStore
  layoutRow: boolean = false

  // Force the pdf viewer to unload the document so we can make large scale changes to it

  A4_PAGE: PdfSizeDocument = {
    size: 'A4',
    pageHeight: 515,
    author: '',
    title: '',
    subtitle: '',
    subject: '',
    orientation: 'landscape',
    brand: 'Centric Software'
  }

  @computed get selectedPdfDocument(): PdfSizeDocument {
    if (!this.config) return null
    const {config: {document}, selectedPageSize} = this
    return _.find(document, d => d?.size === selectedPageSize) ?? this.A4_PAGE //_.isArray(document) ? document.find(d => d?.size === selectedPageSize) : document
  }

  @observable initialPdfRender = true
  _opts

  @observable documentProps
  /**
   * Used to force the PDF viewer to reinitialize as it doesn't always react to prop changes like title
   */
  @observable viewerKey = guid()
  @action updateViewKey = () => {
    this.viewerKey = guid()
    this.isRenderingPdf = true
    return this.viewerKey
  }

  updateViewKey_debounce = _.debounce(() => this.updateViewKey(), 200, {trailing: true, leading: false})

  @computed get isLoading() {
    const {isCreatingPdf, document} = this

    return !document || isCreatingPdf
  }

  @computed get visibleCardFields() {
    const {catalog: {cv: {groupedVisibleCardFields, overriddenVisibleFields}}} = this,
      groupsVisibleFields = Object.keys(groupedVisibleCardFields)
    return overriddenVisibleFields.filter(
      f => groupsVisibleFields.length ? groupsVisibleFields.filter(v => !_.some(groupedVisibleCardFields[v], g => g.field === f.field)).length !== 0 : true)
  }

  @computed.struct get defaultConfigForTemplate() {
    const {reportTemplate, defaultReportTemplateForCustomer} = this
    return _.merge({}, defaultReportTemplateForCustomer, reportTemplate!.defaults)
  }

  @computed get versions() {
    const {catalog: {sortedVersions}, stores: {filter: {state: {groups: {merchSeason}}}}} = this
    let versions = []
    if (!merchSeason) {
      return []
    }
    const {groupedByValue, selected} = merchSeason

    selected.size > 0 && selected.forEach(k => { versions.push(k && k.name) })
    return sortedVersions(selected.size ? versions : Object.keys(groupedByValue || {}).filter(val => val !== FILTER_UNASSIGNED_ID))
  }

  @computed get _selectedCollectionsAsText() {
    const {vlp: {hierarchy: {selectedCollections}}} = this,
      grouped = _.groupBy(selectedCollections, f => _.last<any>(f.subCategories)?.name ?? f.folder?.name ?? f.plan?.name)
    return _.flatMap(
      _.sortBy(_.keys(grouped))
        .map(k => _.sortBy(grouped[k].map(v => v.name)))
    ).join(', ')
  }

  @computed get _selectedFoldersAsText() {
    const {selectedFolders, pathTitleForFolder} = this.vlp.hierarchy
    const _groupLevelFolders = _.groupBy(selectedFolders, 'level')
    return Object.keys(_groupLevelFolders).map(f => {
      const selectedAtIndex = _groupLevelFolders[f],
        grouped = _.groupBy(selectedAtIndex, f => pathTitleForFolder(f.parent?.id) ?? f.plan?.name)
      return _.flatMap(
          _.sortBy(_.keys(grouped)).map(k => _.sortBy(grouped[k].map(f => f.name))))
        .join(', ')
    }).join(' > ')
  }

  @computed get hierarchy(): any {
    const {isPlanHierarchy, vlp} = this
    if (isPlanHierarchy) {
      const {hierarchy: {selectedCollections, selectedPlans, selectedFolders, selectedFoldersAsText, pathTitleForFolder}} = vlp,
        hierarchy = {}, {_selectedCollectionsAsText: collection, _selectedFoldersAsText: folders} = this

      selectedPlans?.forEach(p => hierarchy['plan'] = {plan: p.name})
      hierarchy['folders'] = {folders}
      hierarchy['collection'] = {collection}
      // selectedCollections?.forEach((c: any) => hierarchy['collection'] = {...hierarchy[c.plan.exId], collection: hierarchy[c.plan.exId]?.collection ? `${hierarchy[c.plan.exId].collection}, ${c.name}` : `${c.name}`})
      return hierarchy
      /*
      return {
        plan: (selectedPlans ?? []).map(p => p.name).join(', '),
        folderName: selectedFoldersAsText,
        collection: (selectedCollections ?? []).map(c => c.name).join(', ')
      }*/
    }

    // if (workspaces?.deploymentConfig?.customer?.name === 'ASICS') {
    //   const {hierarchy: {selectedFolder, selectedCollection, selectedPlan}} = vlp
    //   return {plan: selectedPlan?.name, folderName: _.map(selectedFolder.selectedFolder, t => t.name).join(', '), collection: selectedCollection?.name}
    // }
    return {seasonal: site.seasonal}
  }

  @computed get isPlanHierarchy() {
    const {perspective, catalogConfig: {itemCreation: ic, showHierarchyPlan}} = this.catalog,
      creation = perspective?.itemCreation ?? ic
    return creation?.required?.includes('plan') ?? showHierarchyPlan
  }

  @computed get itemCount() {
    return this.query?.data?.total?.value ?? this.catalog.itemCount
  }

  @computed get breadcrumbData() {
    const {isPlanHierarchy, hierarchy, hierarchy: {seasonal}} = this
    //console.log(plan, folderName, collection)

    return isPlanHierarchy ? _.map(hierarchy, ({plan, folders, collection}) => _.without([plan, folders, collection], undefined).join(' > ')) : _.map(seasonal, s => s && s.name).filter(b => b)
  }

  @computed get section() {
    const {sections, config: {selectedSection}} = this
    return sections[selectedSection]
  }

  @computed get reportTemplate(): PdfReportTemplate {
    const {reportTemplates, config, grouping, catalog: {isShowingAssortment}} = this
    return reportTemplates[isShowingAssortment ? 'rh' : grouping?.groupDefinition?.pdf?.template ?? config?.selectedTemplate] ?? reportTemplates[_.keys(reportTemplates)[0]]
  }

  @computed get itemsPerRow(): number {
    const {config: {columns}, cardView, selectedPageSize} = this
    return cardView.one_mpv_row ? 1 : selectedPageSize === A4_PAGE_SIZE ? columns == 'compact' ? 8 : 6 : columns == 'compact' ? 10 : 8
  }

  @computed get timeStamp() {
    return moment().format('ddd MMM D YYYY H:mm:ss')
  }

  @observable limitedCount = 0

  @computed get imageHeight(): number {
    const {config: {imageRatio, columns}} = this
    return imageRatio === 'square' ? this.cardWidth : columns === 'standard' ? imageRatio === 'portrait' ? 160 : 60 : imageRatio == 'portrait' ? 120 : 45
  }

  @computed get cardWidth(): number {
    const {config: {columns}, selectedPageSize} = this
    return columns === 'standard' ? selectedPageSize === A4_PAGE_SIZE ? 120 : 130 : selectedPageSize === A4_PAGE_SIZE ? 90 : 105
  }

  @computed get cardMargin(): number {
    const {config: {columns}, selectedPageSize} = this
    return columns === 'standard' ? 8 : selectedPageSize === A4_PAGE_SIZE ? 5 : 7
  }

  @computed get cardFontSize(): number {
    const {config: {columns}} = this
    return columns === 'standard' ? 8 : 6
  }

  @computed get labelFontSize(): number {
    const {config: {columns}} = this
    return columns === 'standard' ? 6 : 4
  }

  @computed get detailViewMargin(): number {
    const {config: {columns}} = this
    return columns === 'standard' ? 5 : 2
  }

  @computed get lineMargin(): number {
    const {config: {columns, imageRatio}} = this
    return columns === 'compact'
           ? imageRatio === 'square'
             ? 8
             : imageRatio === 'portrait'
               ? 10 : 5
           : imageRatio === 'portrait'
             ? 10
             : imageRatio === 'square'
               ? 5 : 8
  }

  @computed get cardView(): ICardViewSettings {
    const {catalog: {cardView}} = this
    const cardViewValues = _.clone(cardView)
    /**
     * Piyush Bhati - these two values doesn't affect the card height,
     * need to remove before the height calculation on the pdf product card.
     * */
    delete cardViewValues.ir
    delete cardViewValues.all
    return cardViewValues
  }

  @computed get selectedCardView() {
    return _.filter(this.cardView, c => c === true)
  }

  @computed get getLineHeight() {
    const {
      config: {columns},
      visiblePdfCardFields
      , catalog: {settings: {showCardLabels}}
    } = this
    switch (columns) {
      case 'compact':
        return showCardLabels ? 15 : visiblePdfCardFields.length ? 12 : 8
      case 'standard':
        return showCardLabels ? 20 : visiblePdfCardFields.length ? 16 : 12
      default:
        return 0
    }
    // return 0
  }

  @computed get getGroupedViewHeight() {
    const {config: {columns}, getLineHeight, catalog: {cv: {groupedVisibleCardFields}}} = this,
      groupKeys = Object.keys(groupedVisibleCardFields),
      rows = groupKeys.reduce((acc, key) => {
        if (groupedVisibleCardFields[key].length) {
          acc += Math.ceil(groupedVisibleCardFields[key].length / (columns === 'standard' ? 3 : 2))
        }
        return acc
      }, 0)
    return columns === 'standard' ? rows : rows + 1
    // return rows * 20
    // let groupedViewHeight = 0
    // let multiple = 0
    // Object.keys(groupedVisibleCardFields).forEach(group => {
    //   switch (columns) {
    //     case 'compact':
    //       // multiple = Math.ceil(groupedVisibleCardFields[group].length / 2)
    //       groupedViewHeight += getLineHeight * multiple
    //       break
    //     case 'standard':
    //       // multiple = Math.ceil(groupedVisibleCardFields[group].length / 3)
    //       groupedViewHeight += getLineHeight * multiple
    //   }
    // })
    // return groupedViewHeight
  }

  @computed get secondariesSectionsCountsAndHeight() {
    const {secondariesSectionFields, catalog} = this,
      {settings: {numberOfPDFVisibleViewOptions}} = ui
    let result = []
    let heightCount = 0
    let commentTextCount = 0
    secondariesSectionFields?.forEach(f => {
      const sectionConfig = catalog.sectionById[f.section]
      let res = sectionConfig?.id ? (catalog.sectionValues.get(sectionConfig.id) ?? [])
        .filter(({key}) => {
          const lookup = `${sectionConfig?.path}.${sectionConfig?.viewOptions.field}.${key}`
          const isTrue = catalog.cardView.all || catalog.cardView[lookup]
          if (isTrue) {
            if (f?.def?.type && f?.def?.type !== 'String') {
              heightCount++
            } else {
              // color feedback don't contain type and on pdf it take 2 lines extra to render
              commentTextCount++
            }
          }
          return isTrue
        }) : []
      if (!res.length || res.length > numberOfPDFVisibleViewOptions) {
        const notStringType = f?.def?.type && f?.def?.type !== 'String'
        res = [...(Array.isArray(res) && res.length ? res : sectionConfig?.id ? _.toArray(catalog.sectionValues.get(sectionConfig.id)) : [])].splice(0, (notStringType ? numberOfPDFVisibleViewOptions * 3 : numberOfPDFVisibleViewOptions))
        if (notStringType) {
          heightCount = (heightCount / 3) > numberOfPDFVisibleViewOptions ? [...(res ?? [])].splice(0, numberOfPDFVisibleViewOptions * 3).length : res.length
        } else {
          commentTextCount = numberOfPDFVisibleViewOptions
        }
      }
      result = [...result, ...res]
    })
    return {sectionCount: result.length, heightCount, commentTextCount}
  }

  @computed get getSecondariesSectionsHeight() {
    const {secondariesSectionsCountsAndHeight: {heightCount, commentTextCount}, config: {columns}} = this,
      height = Math.ceil(heightCount / (columns === 'standard' ? 3 : 2))
    return columns === 'standard' ? height + (commentTextCount * 2) : height + commentTextCount + 30
  }

  @computed get secondariesSectionFields() {
    // As there are other sections such as mpv,colors etc.
    return this.visiblePdfCardFields?.filter(f => f?.section === uiConstants.SECONDARIES)
  }

  get hierarchyGroups() {
    const {groupBy: {hierarchyGroups}} = this
    return hierarchyGroups
  }

  get groupName() {
    const {catalog: {groupBy: {hierarchy}}} = this
    return _.first(hierarchy)
  }

  @computed get grouping() {
    const {groupName, catalog} = this
    return groupName && catalog.groupBy.groupingByName.get(groupName)
  }

  @computed get hasSubGroup() {
    return this.grouping?.hasSubGroup ?? false
  }

  @computed get hrGroupLineWidth() {
    const {config: {showHrGroupingLines, columns}} = this
    return showHrGroupingLines ? columns === 'standard' ? 2 : 1 : 0  //: showCount ? 40 : 30
  }

  cardHeight: number = 0

  @computed get visibleAttributes() {
    if (!site) return []

    const {
      pdfConfig,
      cv: {overriddenVisibleFields}
    } = this.catalog

    return pdfConfig ? overriddenVisibleFields.filter(v => v.def).slice(0, (pdfConfig?.fieldCount ?? 4) - overriddenVisibleFields.filter(v => !v.def).length) : []
  }

  @computed get columnHeight() {
    const {config: {layout}} = this
    return layout === 'column' ? 30 : 0
  }

  getCalculateRowPerPage = (isProductView) => {
    const {config: {layout}, selectedPdfDocument: {pageHeight}, columnHeight, imageHeight, cardMargin: margin, getLineHeight, getGroupedViewHeight, getSecondariesSectionsHeight, secondariesSectionFields, detailViewMargin, hrGroupLineWidth} = this
    const len = this.visiblePdfCardFields.filter(f => f?.section !== uiConstants.SECONDARIES).length
    const visibleFieldLimit = ui.settings.numberOfPDFVisibleViewOptions
    const explicit = isProductView ? 0 : hrGroupLineWidth //: showCount ? 40 : 30
    const count = len > visibleFieldLimit ? (visibleFieldLimit + getGroupedViewHeight) : len  // : len > 8 ? 8 : len
    const multiple: number = count ? count * getLineHeight : 0
    const sectionHeight = len > visibleFieldLimit ? 0 : secondariesSectionFields.length ? (getSecondariesSectionsHeight ? getSecondariesSectionsHeight * getLineHeight : secondariesSectionFields.length * getLineHeight) : 0
    const column = layout === 'column' ? 8 : 0
    const cardHeight = (imageHeight + (margin * 1.25) + multiple + explicit + sectionHeight + column)

    this.cardHeight = cardHeight
    const result = Math.floor((pageHeight) / cardHeight)

    // const result = Math.floor((pageHeight - columnHeight) / (cardHeight + length))

//    console.log({ result, imageHeight, margin, multiple, explicit, sectionHeight, count })
    return _.isNaN(result) ? 3 : result
  }

  columnsPage = computedFn((size) => {
    const {itemsPerRow, cardWidth, cardMargin} = this

    return (cardWidth + cardMargin) * size
  })

  @computed get rowsPerPage(): number {
    const {getCalculateRowPerPage, catalog: {groupBy: {isProductView}}} = this
    return getCalculateRowPerPage(isProductView)
  }

  @computed get pdfField() {
    const {catalog: {groupBy: {firstGroup, firstGroup: {esField}}}} = this
    return firstGroup.groupName === 'images' ? 'merchOption.name' : esField
  }

  _groupQueryManager: GroupByGroupsQueryManager

  @computed.struct get searchPayload(): any {
    const {
      catalog,
      catalog: {isProductView}
    } = this

    if (!this.config) {
      DEBUG_PDF && console.debug(`${catalog.debugId} - Skip PDF load due to null this.config`)
      return null
    }

    // trace()
    //console.log(`[PDF SearchPayload - ${catalog.debugStoreIdPrefix}`)

    if (isProductView) {
      if (!catalog.hasLoadedConfig || _.isEmpty(catalog.queryVariablesEs)) {
        DEBUG_PDF && console.debug(`${catalog.debugId} - Skip PDF load due to `, {hasLoadedConfig: catalog.hasLoadedConfig, queryVariablesEs: catalog.queryVariablesEs})
        return null
      }

      const {
        catalog: {
          es: {myShouldTermForInlineItemsFirst},
          queryVariablesEs, _sourceDynamic, localLODCatalogItems, settings: {showInlineCatalogItemsFirst}, workspace_offlineItemIds
        }, config: {queryLimit}
      } = this

      let r = {
        ...queryVariablesEs,
        ..._sourceDynamic,
        size: !DEBUG || queryLimit == 0 ? PDF_MAX_ITEMS : queryLimit - workspace_offlineItemIds.length
      }

      if (showInlineCatalogItemsFirst && (localLODCatalogItems ?? []).length) {
        r = {
          ...r,
          query: {
            ...r.query,
            bool: {
              ...r.query.bool,
              ...myShouldTermForInlineItemsFirst
            }
          }
        }
      }

      // if (showOnlyInlineCatalogItems && localLODCatalogItems.length) {
      //   r = _.cloneDeep(r)
      //   r.query = {
      //     bool: {
      //       filter: {
      //         terms: {
      //           exId: localLODCatalogItems
      //         }
      //       }
      //     }
      //   }
      // }

      return r
    } else {
      const {groupBy: {hierarchy}} = catalog
      if (hierarchy.length < 1 || !hierarchy[0]) {
        DEBUG_PDF && console.debug(`${catalog.debugId} - Skip PDF load due to `, {hierarchy})
        return null
      }

      return this._groupQueryManager.queryVariables
    }
  }

  @computed get rhTemplate() {
    return this.catalog?.assortments?.selectedAssortment !== null //pdf.config.selectedTemplate === 'rh'
  }

  generateSource = (subtitle, source, _source) => {
    const values = {..._source}
    if (source && _source) {
      subtitle.forEach(t => {
        const value = source[t.field]?.buckets?.map(b => b.key).join(', ')
        _.set(values, t.field, value)
      })
    }
    return values
  }

  @computed get customSort() {
    const {index, groupBy: {groupingByName}} = this.catalog
    return groupingByName.get(index).orderBy.some(ob => ob.custom)
  }

  @computed get onlyOfflineItems() {
    return this.stores.filter.onlyShowOfflineItems || this.catalog.settings.showOnlyOfflineItems
  }

  @action convertToGroups = async (groupsToRender, finalGroupItemLimit, groupName, groupLength, index, parentStore?) => {
    const {es, customSort, catalog} = this, {esFieldFor_byField, _fieldByName, queryVariablesEs, _sourceDynamic, groupBy: {isProductView, groupingByName}, settings: {activeSearchText}} = catalog,
      grouping = groupingByName.get(groupName)
    const totalOfflineItems = _.values(grouping.groupOfflineItems).reduce((acc, item) => acc += item.length, 0)
    const isFirstGroup = !index && groupLength === 2,
      multiGroupQueryBody = (groupsToRender ?? []).reduce((acc, {keys, count}, i) => {
        let body = {
          ..._.cloneDeep(queryVariablesEs),
          ..._sourceDynamic,
          size: isFirstGroup ? 0 : i + 1 == groupsToRender.length ? finalGroupItemLimit <= 0 ? (count + finalGroupItemLimit) : finalGroupItemLimit : count
        }
        keys && (customSort && body?.query?.function_score ? body.query.function_score.query.bool.filter : body?.query?.bool?.filter).push({
          bool: {
            filter: Object.keys(keys).reduce((acc, k) => {
              const v = keys[k]

              if (_fieldByName[k]?.agg === 'exists') {
                if (v) {
                  return [
                    ...acc,
                    ...(parentStore?.mustTerms ?? []), {bool: {must: {exists: {field: k}}}}
                  ]
                }
                return [
                  ...acc,
                  ...(parentStore?.mustTerms ?? []), {bool: {must_not: {exists: {field: k}}}}
                ]
              }

              if (v === null) {
                return [
                  ...acc,
                  ...(parentStore?.mustTerms ?? []), {bool: {must_not: {exists: {field: k}}}}
                ]
              } else {
                if (parentStore) {
                  return [
                    ...acc,
                    ...parentStore.mustTerms, {
                      term: {
                        [esFieldFor_byField(k, true)]: keys[k]
                      }
                    }
                  ]
                }
                return [
                  ...acc, {
                    term: {
                      [esFieldFor_byField(k, true)]: keys[k]
                    }
                  }
                ]
              }
            }, [])
          }
        })
        acc.push({
          index: catalog.index
        })
        acc.push(body)
        return acc
      }, []),
      {responses: multiGroupResults} = await (multiGroupQueryBody.length > 0 ? es.msearch({index: catalog.index, body: multiGroupQueryBody, meta: 'multiple search to get group and data in pdf'}) : Promise.resolve({responses: [], timeout: 0})),
      // Convert them to the form that the existing group by page expects from a traditional query
      isBoolean = _fieldByName[groupName]?.def?.type === 'Boolean',
      converted = (groupsToRender ?? []).map((g, i) => {
        const offlineItems = activeSearchText ? [] : this.grouping.groupOfflineItems[this.grouping?.fieldDefinition?.agg === 'exists' ? g.title === 'Yes' : g.title === 'Unassigned' ? isBoolean ? 'false' : UNASSIGNED : g.title] ?? [],
          offlineLength = offlineItems?.length ?? 0,
          result = multiGroupResults[i] ? es.unwrapHitsResult(multiGroupResults[i]) : null

        return this.onlyOfflineItems ? g.offline_count ? {
          count: offlineLength,
          key: g.key,
          keys: g.keys,
          title: g.title,
          groupName,
          items: {
            items: [...offlineItems],
            total: {
              value: offlineLength
            }
          },
          total_doc_count: totalOfflineItems
        } : undefined : result ? {
          ...g,
          aggs: g.aggs ?? {},
          count: g.count + offlineLength,
          groupName,
          parentStore,
          items: {
            ...result,
            items: [...(offlineItems), ...(result.items ?? [])],
            total: {...(result?.total ?? 0), value: (result.total?.value ?? 0) + offlineLength}
          },
          total_doc_count: g.count + offlineLength
        } as IPdfGroupBySearchResult_Group : undefined
      }).filter(_.identity)
    return {converted, totalOfflineItems, groupResults: multiGroupResults}
  }

  @computed get horizontalMargin() {
    const {config: {layout, columns}, visibleCardFields, cardMargin} = this
    if (layout === 'column') {
      switch (columns) {
        case 'compact': {
          return 25
        }
        case 'standard': {
          return cardMargin
        }
      }
    }
    return 0
  }

  sequenceArrayPromises = (array, fn) => {
    return array.reduce(function (p, item) {
      return p.then(function () {
        return fn(item)
      })
    }, Promise.resolve())
  }

  @action convertGroups = async (groups, converted) => {
    let multiGroupQueryBody = [],
      {es, customSort, catalog} = this,
      {esFieldFor_byField, _fieldByName, queryVariablesEs, _sourceDynamic, settings: {activeSearchText}, groupBy: {groupingByName}} = catalog
    for (let c of converted) {
      const group = groups.get(c.key),
        {groupsToRender, finalGroupItemLimit, groupName, groupLength, index, parentStore} = group,
        isFirstGroup = !index && groupLength === 2
      multiGroupQueryBody = [
        ...multiGroupQueryBody,
        ...(groupsToRender ?? []).reduce((acc, {keys, count}, i) => {
          let body = {
            ..._.cloneDeep(queryVariablesEs),
            ..._sourceDynamic,
            size: isFirstGroup ? 0 : i + 1 == groupsToRender.length ? finalGroupItemLimit <= 0 ? (count + finalGroupItemLimit) : finalGroupItemLimit : count
          }

          keys && (customSort && body?.query?.function_score ? body.query.function_score.query.bool.filter : body?.query?.bool?.filter).push({
            bool: {
              filter: Object.keys(keys).reduce((acc, k) => {
                const v = keys[k]

                if (_fieldByName[k]?.agg === 'exists') {
                  if (v) {
                    return [
                      ...acc,
                      ...(parentStore?.mustTerms ?? []), {bool: {must: {exists: {field: k}}}}
                    ]
                  }
                  return [
                    ...acc,
                    ...(parentStore?.mustTerms ?? []), {bool: {must_not: {exists: {field: k}}}}
                  ]
                }

                if (v === null) {
                  return [
                    ...acc,
                    ...(parentStore?.mustTerms ?? []), {bool: {must_not: {exists: {field: k}}}}
                  ]
                } else {
                  if (parentStore) {
                    return [
                      ...acc,
                      ...parentStore.mustTerms, {
                        term: {
                          [esFieldFor_byField(k, true)]: keys[k]
                        }
                      }
                    ]
                  }
                  return [
                    ...acc,
                    ...parentStore.mustTerms, {
                      term: {
                        [esFieldFor_byField(k, true)]: keys[k]
                      }
                    }
                  ]
                }
              }, [])
            }
          })
          acc.push({})
          acc.push(body)
          return acc
        }, [])
      ]
    }

    if (multiGroupQueryBody.length) {
      const {responses: multiGroupResults} = await es.msearch({index: catalog.index, body: multiGroupQueryBody, meta: 'group by group data for pdf'})
      const result = []
      let count = 0
      for (let c of converted) {
        const group = groups.get(c.key),
          {groupsToRender, finalGroupItemLimit, groupName, groupLength, index, parentStore} = group,
          grouping = groupingByName.get(groupName),
          totalOfflineItems = _.values(grouping.groupOfflineItems).reduce((acc, item) => acc += item.length, 0)

        const children = groupsToRender.map((g, i) => {
          const offlineItems = activeSearchText ? [] : grouping.groupOfflineItems[g.title === 'Unassigned' ? UNASSIGNED : grouping?.fieldDefinition?.agg === 'exists' ? g.title === 'Yes' : g.title] ?? [],
            offlineLength = offlineItems?.length ?? 0,
            result = multiGroupResults[count] ? es.unwrapHitsResult(multiGroupResults[count]) : null
          count++
          return this.onlyOfflineItems ? g.offline_count ? {
            count: offlineLength,
            key: g.key,
            keys: g.keys,
            title: g.title,
            groupName,
            items: {
              items: [...offlineItems],
              total: {
                value: offlineLength
              }
            },
            total_doc_count: totalOfflineItems
          } : undefined : result ? {
            ...g,
            aggs: g.aggs ?? {},
            count: g.count + offlineLength,
            groupName,
            parentStore,
            items: {
              ...result,
              items: [...(offlineItems), ...(result.items ?? [])],
              total: {...(result?.total ?? 0), value: (result.total?.value ?? 0) + offlineLength}
            },
            total_doc_count: g.count + offlineLength
          } as IPdfGroupBySearchResult_Group : undefined
        })
        result.push({...c, children, childrenOffline: totalOfflineItems})
      }
      return result
    }
  }

  @action getSubGroupData = async () => {
    const {query, es: {getItemById}} = this
    const assortmentRootStore = new AssortmentRootStore(this.stores, {})
    await assortmentRootStore.runQuery()
    const {cardsByIndex} = assortmentRootStore
    query.data = await Promise.all(_.range(0, cardsByIndex.size).map(async (i) => {
      const data = cardsByIndex.get(i),
        {assortment: {dataMap: {images}}, getHeaderImageForeignKeyValue} = assortmentRootStore
      if (!_.isString(images) && images && images?.foreignField && images?.esIndex) {
        const {foreignField, esIndex} = images,
          id = getHeaderImageForeignKeyValue(data.item)
        if (id) {
          await assortmentRootStore.lookupRemoteObject({field: foreignField, esIndex, id})
        }
        return new PDFHeroCardStore(this.stores, data, assortmentRootStore)
      }
    }))
    this.pdfData = query.data
    return this.query
  }

  @computed get queryLimit() {
    const {config: {queryLimit}} = this
    return !DEBUG ? PDF_MAX_ITEMS : queryLimit
  }

  @observable.ref searchCancelToken: CancelTokenSource
  @action search = async () => {
    const {stores, es, catalog, searchPayload, isFrozen, query, undo, _groupQueryManager, onlyOfflineItems, queryLimit} = this,
      {localLODCatalogItems, filteredOfflineItems, groupBy: {isProductView}, settings: {showInlineCatalogItemsFirst, activeSearchText}} = catalog

    if (!isFrozen && searchPayload) {
      DEBUG_PDF && console.debug(`${catalog.debugId} - Starting PDF query`, toJS({isFrozen, searchPayload}))

      // this._lastSearchPayload = _.cloneDeep(searchPayload)

      query.loading = true
      undo.saveUndoState()

      if (this.searchCancelToken) {
        DEBUG_PDF && console.debug(`Cancelling and refetching PDF data`)
        this.searchCancelToken.cancel(`search() reentrancy`)
      }

      try {
        if (isProductView && !catalog.isShowingAssortment) {
          this.searchCancelToken = axios.CancelToken.source()
          const searchResult = this.onlyOfflineItems ? {} : await es.search({
              meta: `PDFExportStore.search() isProductView ${isProductView}`,
              cancelToken: this.searchCancelToken.token,
              body: searchPayload,
              index: catalog.index
            }, {log: {query: false, result: false}}),
            offline = activeSearchText ? [] : filteredOfflineItems,
            res = !this.onlyOfflineItems && es.unwrapHitsResult(searchResult)

          this.searchCancelToken = null
          let data = this.onlyOfflineItems ? {
            aggregations: undefined,
            items: [...offline],
            total: {
              value: offline.length,
              relation: 'eq'
            }
          } : {
            ...res,
            items: [...offline, ...res.items],
            total: {
              ...res.total,
              value: res.total.value + offline.length
            }
          }
          if (localLODCatalogItems?.length && showInlineCatalogItemsFirst) {
            const d = _.cloneDeep(data),
              [first, rest] = _.partition(d.items, (item) => !!localLODCatalogItems.find(i => i === (item.id ?? item._id ?? item.exId)))
            data = {
              ...d,
              items: [
                ...first,
                ...rest
              ]
            }
          }
          query.data = data
          this.pdfData = query.data
        } else {
          if (catalog.isShowingAssortment) {
            return await this.getSubGroupData()
          }
          let data = await _groupQueryManager.maybeRunQuery() ?? [],
            groups = data,
            {groupBy: {hierarchyGroups, settings: {showOnlyExpanded, showExpandedFirst}}} = catalog
          if (showOnlyExpanded) {
            groups = groups.filter(g => this.grouping.isExpanded(g.key, this.grouping.store?.viewStore?.mgr?.path ?? []))
          } else if (showExpandedFirst) {
            groups = _.orderBy(data, g => !this.grouping.isExpanded(g.key, this.grouping.store?.viewStore?.mgr?.path ?? []))
          }
          groups = _groupQueryManager.shuffleEmptyUnassignedToLastOfBucket(groups)
          const totalCount = _.sum(groups?.map(g => g.count)),
            // Find running totals until query limit is hit
            runningTotals = groups?.reduce((acc, g, i) => queryLimit && i > 0 && _.last(acc) >= queryLimit ? acc : [...acc, ((onlyOfflineItems ? 0 : g.count) + (g?.offline_count ?? 0)) + (i == 0 ? 0 : acc[i - 1])], []) ?? [],
            groupsToRender = runningTotals.length == groups?.length ? groups : groups?.slice(0, runningTotals.length),
            finalGroupItemLimit = !queryLimit || _.last(runningTotals) <= queryLimit ? 0 : runningTotals.length == 1 ? queryLimit : queryLimit - runningTotals[runningTotals.length - 1]

          const {converted, totalOfflineItems} = await this.convertToGroups(groupsToRender, finalGroupItemLimit, this.groupName, hierarchyGroups?.length, 0)
          DEBUG_PDF && console.log({totalCount, groups, queryLimit, finalGroupItemLimit, runningTotals, groupsToRender, converted})
          let children = converted,
            storesGroups = new Map<string, any>(),
            groupQuery = []
          if (hierarchyGroups?.length > 1) {
            children = []
            let limit = queryLimit

            for (const c of converted) {
              const parentGroupStore = new GroupByGroupStore(stores, {
                level: 0,
                groupCount: groups.length,
                group: _.toString(0),
                groupName: this.groupName,
                path: [c.key],
                info: _.omit(c, ['items', 'parentStore', 'total_doc_count']) as IGroupByQueryManagerResult
              })
              const {childGroupStore: {path, groupName, level, mgr: _childQueryManager}, mustTerms, mustNotTerms} = parentGroupStore

              groupQuery.push({})
              groupQuery.push(_childQueryManager.queryVariablesBody)
              storesGroups.set(c.key, parentGroupStore)
            }
            const {responses} = await es.msearch({index: catalog.index, body: groupQuery, meta: '2nd level groups for first level groups'})
            const childrenGroupsToRender = new Map<string, any>()
            for (let i = 0; i < converted.length; i++) {
              const c = converted[i],
                parentGroupStore = storesGroups.get(c.key),
                esResult = responses[i]

              const {childGroupStore: {groupName, mgr: _childQueryManager}} = parentGroupStore
              let childGroups = _childQueryManager.transformResults({esResult})
              if (showOnlyExpanded) {
                childGroups = childGroups.filter(({key}) => _childQueryManager.grouping.isExpanded(key, [c.key]))
              }
              childGroups = _childQueryManager.shuffleEmptyUnassignedToLastOfBucket(childGroups)
              const childRunningTotals = childGroups?.reduce((acc, g, i) => limit && i > 0 && _.last(acc) >= limit ? acc : [...acc, (g.count + (g?.offline_count ?? 0)) + (i == 0 ? 0 : acc[i - 1])], []) ?? [],
                sum: number = _.last(childRunningTotals),
                childGroupsToRender = childRunningTotals.length == childGroups?.length ? childGroups : childGroups?.slice(0, childRunningTotals.length),
                finalGroupLimit = !limit || (sum <= limit) ? 0 : childRunningTotals.length === 1 ? limit : limit - sum

              childrenGroupsToRender.set(c.key, {groupsToRender: childGroupsToRender, finalGroupItemLimit: finalGroupLimit ?? 0, groupName, groupLength: hierarchyGroups?.length, index: 1, parentStore: parentGroupStore})
              // const {converted: childConverted, totalOfflineItems: childOfflineItems} = await this.convertToGroups(childGroupsToRender, finalGroupLimit, groupName, hierarchyGroups?.length, 1, parentGroupStore)
              limit = limit - sum
              // children.push({...c, children: childConverted, childrenOffline: childOfflineItems})
            }
            children = await this.convertGroups(childrenGroupsToRender, converted)
            DEBUG_PDF && console.log({children, 'level': 2})
          }
          if (localLODCatalogItems?.length && showInlineCatalogItemsFirst) {
            const d = _.cloneDeep(children)
            d.map(g => {
              const {children, items} = g
              if (children) {
                children.map((c, index) => {
                  if (c?.items?.items) {
                    const {items: {items}} = c
                    const [first, rest] = _.partition(items, (item) => !!localLODCatalogItems.find(i => i === (item.id ?? item._id ?? item.exId)))
                    g.children[index].items.items = [
                      ...first,
                      ...rest
                    ]
                  }
                  return c
                })
              } else if (items?.items) {
                let {items} = g.items
                const [first, rest] = _.partition(items, (item) => !!localLODCatalogItems.find(i => i === (item.id ?? item._id ?? item.exId)))
                g.items.items = [
                  ...first,
                  ...rest
                ]
              }
            })
            children = d
          }
          query.data = {
            groups: children,
            total: {value: this.onlyOfflineItems ? totalOfflineItems : (_groupQueryManager.itemCount + totalOfflineItems), relation: 'eq'}
          } as IPdfGroupBySearchResult
          this.pdfData = query.data
          return query
        }
      } catch (err) {
        console.error(err)
        onSiteError(err)
      } finally {
        query.loading = false
      }
    } else if (DEBUG_PDF) {
      if (isFrozen) console.debug(`${this.catalog.debugId} - Skipped PDF query due to frozen state`)
      if (!searchPayload) console.debug(`${this.catalog.debugId} - Skipped PDF query due to no searchPayload`)
    }
  }

  @computed get showPDFDownloadButton() {
    const {query: {data}, isRenderingPdf, isLoading} = this
    return (data?.total?.value || Array.isArray(data)) && (!(isLoading || isRenderingPdf))
  }

  @computed get pdfName() {
    const {timeStamp, catalog: {pdfConfig: {pdfDownloadName}}} = this

    return `${pdfDownloadName ?? 'CB_PDF'}_${timeStamp}.pdf`
  }

  private pdfData: IPdfGroupBySearchResult

  @action downloadPdf = async () => {
    this.downloading = true
    /*let MyDoc: any

    let returnChildren
    const {query: {data}, pdfData, reportTemplate, documentProps, search, timeStamp, catalog} = this,
      {pdfConfig: {pdfDownloadName}} = catalog,
      products = pdfData ?? data,
      hasValue = products?.total?.value > 0 || Array.isArray(products)
    if (hasValue) {
      returnChildren = hasValue ? reportTemplate.renderReportTemplate({data: products, download: true}) : reportTemplate.renderReportTemplate({data: []})
      if (returnChildren) {
        return this.getPdfDocument(returnChildren, documentProps)
        // if (MyDoc) {
        //   const blob = await rendererPDF(MyDoc).toBlob()
        //   // create a blobURI pointing to our Blob
        //   link.href = URL.createObjectURL(blob)
        // }
      }
    }*/
    const link = window.document.createElement('a'),
      {pdfName, viewerKey} = this,
      iframe: HTMLIFrameElement = document.querySelector(`iframe[data-viewer-key="${viewerKey}"]`),
      blob = iframe ? iframe.src : null

    if (blob) {
      link.href = blob

      // //todo - move concept into config
      link.download = pdfName
      window.document.body.append(link)
      link.click()
      link.remove()
    }
    this.downloading = false
  }

  getPdfDocument = (children, props) => {
    const {stores} = this
    return (<Document {...props}><StoresInstanceContext.Provider value={stores}>{children}</StoresInstanceContext.Provider></Document>)
  }

  renderPage = (props: PdfPageProps & any) => {
    const {reportTemplate, selectedPdfDocument: {size, orientation}} = this

    let p = {size, orientation, ...props}

    return reportTemplate?.renderPage ? reportTemplate.renderPage(p) : <ReportPage {...p} />
  }

  isComputedField = (field: ICatalogFieldConfig): boolean => {
    return !!field?.computed
  }

  computedField = (field: ICatalogFieldConfig): DetailsFieldComputedType => {
    return field?.computed
  }

  computedValue = (d, field) => {
    const {computedField} = this
    const {expression: {field: f, fields, path, compute}} = computedField(field)
    if (compute && d) {
      return compute(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, f)) ? _.toNumber(_.get(v, f) ?? 0) : _.get(v, f)) : fields?.map(f => _.toNumber(_.get(d, f) ?? 0)))
    }
    return null
  }

  labelForFieldValue = (field: ICatalogFieldConfig, value: any) => {
    const {def} = field
    let label
    if (value === FILTER_UNASSIGNED_ID) {
      label = def?.type === 'Boolean' ? 'No' : 'None'
    } else if (def?.type === 'Double') {
      label = def?.precision ? `${value?.toLocaleString(undefined, {maximumFractionDigits: def?.precision})} ${def?.suffix ?? ''}` : value
    } else {
      label = def?.type === 'Boolean' && !_.isNull(value) ? String(value).replace(/true/g, 'Yes').replace(/false/g, 'No') : this.catalog.attrValueLabelFor({v: value, def})
    }
    return label
  }

  getPdfProductsFilterByGroup = (pdfData) => {
    const {catalog: {idFor, localLODCatalogItems, settings: {perItem, showOnlyInlineCatalogItems}}} = this
    const data = _.clone(pdfData)
    return showOnlyInlineCatalogItems && localLODCatalogItems.length ? (data as IPdfProductSearchResult)?.items ? {
      ...data, items: (data as IPdfProductSearchResult).items.filter(p => {
        const idWithPath = idFor(p),
          localItemData = perItem[idWithPath]
        return localItemData?.lod
      })
    } : (data as IPdfGroupBySearchResult)?.groups ? {
      ...data, groups: (data as IPdfGroupBySearchResult).groups.map(group => {
        const {children, items} = group
        if (children) {
          children.map((c, index) => {
            if (c?.items?.items) {
              const {items: {items}} = c
              group.children[index].items.items = items.filter(p => {
                const idWithPath = idFor(p),
                  localItemData = perItem[idWithPath]
                return localItemData?.lod
              })
            }
            return c
          })
        } else if (items?.items) {
          const {items: {items}} = group
          group.items.items = items.filter(p => {
            const idWithPath = idFor(p),
              localItemData = perItem[idWithPath]
            return localItemData?.lod
          })
        }
        return group
      })
    } : data : data
  }

  /**
   * To identify begin/end of queries for a user
   */
  queryId: string
  pdfStartTime: Date
  @action mark_startPdfCreation = async () => {
    this.queryId = guid()
    this.pdfStartTime = new Date()
    const {es, stores: {currentUser}, queryId, reportTemplate, searchPayload} = this
    es.genericUpsert({
      index: es.indexes.performanceLog,
      body: {
        action: 'PDF Render Started',
        user: currentUser.info?.id,
        template: reportTemplate.label,
        query: JSON.stringify(searchPayload),
        queryId
      }
    })
  }

  @action mark_endPdfCreation = async ({pages}: { pages: number }) => {
    const {es, stores: {currentUser}, queryId, reportTemplate, searchPayload} = this,
      endTime = new Date(), diff = endTime.getTime() - this.pdfStartTime.getTime()

    es.genericUpsert({
      index: es.indexes.performanceLog,
      body: {
        action: 'PDF Render Finished',
        user: currentUser.info?.id,
        template: reportTemplate.label,
        query: JSON.stringify(searchPayload),
        took: diff,
        pages,
        queryId
      }
    })

    this.queryId = null
  }
}

export const usePdfStore = () => {
  const {pdf} = useStores() ?? globalStores
  return pdf
}


// WEBPACK FOOTER //
// ./src/modules/pdf/PdfExportStore.tsx